import BugReportIcon from '@mui/icons-material/BugReport';
import FeedbackIcon from '@mui/icons-material/Feedback';
import SecurityIcon from '@mui/icons-material/Security';
import { Avatar, Card, CardContent, Grid, Theme, Typography } from '@mui/material';
import { LightbulbOnOutline } from 'mdi-material-ui';
import React from 'react';
import { WithTranslation, withTranslation } from 'react-i18next';
import { compose } from 'recompose';

import { WithStyles, withStyles } from '@/hocs/with-styles';

const styles = (theme: Theme) => ({
  avatar: {
    margin: 10,
    color: theme.palette.getContrastText(theme.palette.primary.main),
    backgroundColor: theme.palette.primary.main,
  },
  flexContainer: {
    display: 'flex',
    alignItems: 'center',
    [theme.breakpoints.down('sm')]: {
      flexWrap: 'wrap',
    },
  },
});
interface Properties {}
interface State {}
class FeedbackPage extends React.Component<Properties & WithTranslation & WithStyles<typeof styles>, State> {
  render() {
    const { t, theme, classes } = this.props;
    return (
      <Grid container style={{ marginBottom: 1 / 2 }} spacing={1} alignItems="stretch" justifyContent="flex-start">
        <Grid item xs={12} sm={12} md={12}>
          <Card elevation={1}>
            <CardContent>
              <div className={classes.flexContainer}>
                <Avatar className={classes.avatar}>
                  <FeedbackIcon />
                </Avatar>
                <Typography variant="h3">{t(['shared:feedback'], { defaultValue: 'Feedback' })}</Typography>
              </div>
              <Grid
                container
                style={{ marginBottom: 1 / 2 }}
                spacing={1}
                alignItems="stretch"
                justifyContent="flex-start"
              >
                <Grid item xs={12} sm={6} md={6} lg={4}>
                  <Typography variant="h5" style={{ marginTop: theme.spacing(2) }}>
                    <LightbulbOnOutline style={{ marginRight: '0.25em' }} />
                    {t(['feedback:ImprovementsAndFeatureRequestsTitle'], {
                      defaultValue: 'Improvements & Feature Requests',
                    })}
                  </Typography>
                  {t(['feedback:ImprovementsAndFeatureRequestsText'], {
                    defaultValue:
                      'We are always looking for ways to improve Blackbird with enhancements and new features. A core part of our culture at Blackbird is to listen carefully to the feedback that we get so we can focus on the things that add value for you. We are always open to hearing suggestions, so feel free to submit your feedback at support@blackbird.online, and we will get back to you.',
                  })}
                </Grid>
                <Grid item xs={12} sm={6} md={6} lg={4}>
                  <Typography variant="h5" style={{ marginTop: theme.spacing(2) }}>
                    <BugReportIcon /> {t(['feedback:bugReportsTitle'], { defaultValue: 'Bug Reports' })}
                  </Typography>
                  {t(['feedback:bugReportsText'], {
                    defaultValue:
                      'Blackbird is continuously improving and pushing the limits of what is capable. We place a high value on delivering quality features and the best experience. Unfortunately, sometimes a mistake slips through our automated and manual quality assurance processes. If you spot a bug, please reach out to us to fix it as quickly as possible. A good bug report contains the following elements:',
                  })}
                  <ol>
                    <li>{t(['feedback:bugReportsListSummery'], { defaultValue: 'A description of the issue.' })}</li>
                    <li>
                      {t(['feedback:bugReportsListStepsToReproduce'], {
                        defaultValue:
                          'Steps to reproduce the bug. Please include which browser you are using and if possible the version.',
                      })}
                    </li>
                    <li>
                      {t(['feedback:bugReportsListExpectedResult'], { defaultValue: 'What did you expect to happen?' })}
                    </li>
                    <li>
                      {t(['feedback:bugReportsListActualResults'], {
                        defaultValue: 'What happened instead? Screenshots can be very helpful!',
                      })}
                    </li>
                    <li>
                      {t(['feedback:bugReportsListImpact'], { defaultValue: 'What is the impact of the bug to you?' })}
                    </li>
                  </ol>
                  {t(['feedback:bugReportsTextEnd'], {
                    defaultValue:
                      "Bug reports can be submitted by email to support@blackbird.online with the subject starting with 'Bug Report: '.",
                  })}
                </Grid>
                <Grid item xs={12} sm={6} md={6} lg={4}>
                  <Typography variant="h5" style={{ marginTop: theme.spacing(2) }}>
                    <SecurityIcon /> {t(['feedback:securityReportsTitle'], { defaultValue: 'Security Reports' })}
                  </Typography>
                  {t(['feedback:securityReportsText'], {
                    defaultValue:
                      "We care deeply about security at Blackbird. If you suspect you might have found a security issue, please reach out to us immediately at support@blackbird.online so we can fix it immediately! If you start the subject with 'Security/Abuse Report: ' we can make sure to prioritize it as quickly as possible. We kindly ask you to follow standard disclosure protocols and not publicly disclose a vulnerability until we have released a fix. Please do not attempt to carry out Denial-of-Service attacks. We also discourage the use of any vulnerability testing tools that automatically generate very significant volumes of traffic.",
                  })}
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    );
  }
}
const enhance = compose<unknown, Properties>(
  withTranslation(['shared', 'feedback']),
  withStyles(styles, { withTheme: true }),
);
export default enhance(FeedbackPage as React.ComponentType<unknown>);
